import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Logo } from '@/components/Logo';
import { ArrowLeft, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/integrations/supabase/client';
import { validatePassword, mapPasswordError } from '@/lib/passwordValidation';
import { PasswordRequirements } from '@/components/PasswordRequirements';
import { useAuth } from '@/hooks/useAuth';

type Step = 'email' | 'code' | 'legacy-link' | 'success';

function parseAuthError(): { code: string; description: string } | null {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const error = search.get('error') || hash.get('error');
  if (!error) return null;
  const code = search.get('error_code') || hash.get('error_code') || error;
  const descRaw = search.get('error_description') || hash.get('error_description') || '';
  const description = decodeURIComponent(descRaw.replace(/\+/g, ' '));
  return { code, description };
}

function getAuthParams() {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return {
    code: search.get('code') || hash.get('code'),
    tokenHash: search.get('token_hash') || hash.get('token_hash'),
    type: search.get('type') || hash.get('type'),
  };
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const { clearPasswordReset } = useAuth();

  // Step state. Default = email entry.
  const [step, setStep] = useState<Step>('email');
  const [legacyChecking, setLegacyChecking] = useState(true);

  // Email step
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [isSending, setIsSending] = useState(false);

  // Code step
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Legacy-link error display
  const [linkError, setLinkError] = useState<{ code: string; description: string } | null>(null);

  // Detect legacy email links (token_hash / code / hash fragment) on mount.
  // If present, run the old verify path. Otherwise go straight to the new flow.
  useEffect(() => {
    let isSubscribed = true;

    const err = parseAuthError();
    if (err) {
      setLinkError(err);
      setStep('legacy-link');
      setLegacyChecking(false);
      window.history.replaceState({}, '', '/reset-password');
      return;
    }

    const { code: legacyCode, tokenHash, type } = getAuthParams();

    const finish = (ok: boolean, errMsg?: string) => {
      if (!isSubscribed) return;
      if (ok) {
        // Legacy link successfully verified -> jump straight to setting password.
        setStep('code');
        // We don't have the email at this point but updateUser works on the session.
        setCode('__legacy__'); // sentinel: skip verifyOtp on submit
      } else {
        setLinkError({ code: 'verification_failed', description: errMsg || 'Link is invalid or expired.' });
        setStep('legacy-link');
      }
      setLegacyChecking(false);
      window.history.replaceState({}, '', '/reset-password');
    };

    if (tokenHash && (!type || type === 'recovery')) {
      supabase.auth
        .verifyOtp({ token_hash: tokenHash, type: 'recovery' })
        .then(({ data, error }) => finish(!error && !!data?.session, error?.message));
    } else if (legacyCode) {
      supabase.auth
        .exchangeCodeForSession(legacyCode)
        .then(({ data, error }) => finish(!error && !!data?.session, error?.message));
    } else {
      // Listen briefly for legacy PASSWORD_RECOVERY (hash fragment flow)
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
        if (!isSubscribed) return;
        if (event === 'PASSWORD_RECOVERY') {
          subscription.unsubscribe();
          finish(true);
        }
      });
      // No legacy params -> show the new email-entry step immediately.
      setLegacyChecking(false);
      return () => {
        isSubscribed = false;
        subscription.unsubscribe();
      };
    }

    return () => {
      isSubscribed = false;
    };
  }, []);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Please enter your email address');
      return;
    }
    setIsSending(true);
    const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setIsSending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success('Check your inbox for your password reset code.');
    setEmail(trimmed);
    setStep('code');
  };

  const handleResendCode = async () => {
    if (!email.trim()) {
      toast.error('Enter your email first');
      return;
    }
    setIsSending(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setIsSending(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('A new code is on its way.');
    }
  };

  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();

    const isLegacy = code === '__legacy__';

    if (!isLegacy) {
      const trimmedCode = code.replace(/\s+/g, '');
      if (trimmedCode.length < 6 || trimmedCode.length > 10) {
        toast.error('Enter the code from your email');
        return;
      }
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    // Step A: verify the code (skipped for legacy-link path, which already has a session)
    if (!isLegacy) {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: code.replace(/\s+/g, ''),
        type: 'recovery',
      });
      if (verifyError) {
        setIsSubmitting(false);
        toast.error(verifyError.message || 'Invalid or expired code');
        return;
      }
    }

    // Step B: set the new password against the recovery session
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setIsSubmitting(false);
      toast.error(mapPasswordError(updateError.message));
      return;
    }

    // Step C: clear the throwaway recovery session
    await supabase.auth.signOut();
    clearPasswordReset();
    setStep('success');
    setIsSubmitting(false);
  };

  // ---------- Renders ----------

  if (legacyChecking) {
    return (
      <div className="min-h-screen gradient-dark flex flex-col items-center justify-start pt-8 md:pt-24 px-6">
        <div className="w-full max-w-md">
          <div className="gradient-card rounded-2xl border border-border/50 p-8 shadow-card text-center">
            <div className="flex justify-center mb-6"><Logo /></div>
            <h1 className="text-2xl font-bold mb-6">Reset your password</h1>
            <div className="flex justify-center">
              <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen gradient-dark flex flex-col items-center justify-start pt-8 md:pt-24 px-6">
        <div className="w-full max-w-md">
          <div className="gradient-card rounded-2xl border border-border/50 p-8 shadow-card text-center">
            <div className="flex justify-center mb-6"><Logo /></div>
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="w-14 h-14 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Password updated</h2>
            <p className="text-muted-foreground">
              Return to the PodBrief app and sign in with your new password.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'legacy-link') {
    const isExpired = linkError?.code === 'otp_expired' || /expired/i.test(linkError?.description || '');
    return (
      <div className="min-h-screen gradient-dark flex flex-col items-center justify-start pt-8 md:pt-24 px-6">
        <div className="w-full max-w-md">
          <div className="gradient-card rounded-2xl border border-border/50 p-8 shadow-card text-center">
            <div className="flex justify-center mb-6"><Logo /></div>
            <div className="flex justify-center mb-4">
              <AlertCircle className="w-14 h-14 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold mb-3">
              {isExpired ? 'Reset link expired' : 'Reset link invalid'}
            </h1>
            <p className="text-muted-foreground mb-6">
              No problem — request a new code below and enter it on the next screen.
            </p>
            <Button
              type="button"
              onClick={() => {
                setLinkError(null);
                setStep('email');
              }}
              className="w-full h-12 gradient-gold text-primary-foreground font-semibold shadow-glow hover:opacity-90"
            >
              Get a new code
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'email') {
    return (
      <div className="min-h-screen gradient-dark flex flex-col items-center justify-start pt-8 md:pt-24 px-6">
        <div className="w-full max-w-md">


          <div className="gradient-card rounded-2xl border border-border/50 p-8 shadow-card">
            <div className="flex justify-center mb-6"><Logo /></div>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-2">Reset your password</h1>
              <p className="text-muted-foreground">
                Enter your email and we’ll send you a password reset code.
              </p>
            </div>

            <form onSubmit={handleSendCode} className="space-y-4">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                autoComplete="email"
                autoFocus
              />
              <Button
                type="submit"
                disabled={isSending}
                className="w-full h-12 gradient-gold text-primary-foreground font-semibold shadow-glow hover:opacity-90"
              >
                {isSending ? 'Sending...' : 'Send code'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // step === 'code'
  const isLegacy = code === '__legacy__';
  return (
    <div className="min-h-screen gradient-dark flex flex-col items-center justify-start pt-8 md:pt-24 px-6">
      <div className="w-full max-w-md">
        <button
          type="button"
          onClick={() => setStep('email')}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Use a different email
        </button>

        <div className="gradient-card rounded-2xl border border-border/50 p-8 shadow-card">
          <div className="flex justify-center mb-6"><Logo /></div>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Set a new password</h2>
            <p className="text-muted-foreground">
              {isLegacy
                ? 'Choose a new password for your account.'
                : <>We sent a password reset code to <span className="text-foreground">{email}</span>.</>}
            </p>
          </div>

          <form onSubmit={handleSubmitCode} className="space-y-4">
            {!isLegacy && (
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={10}
                placeholder="Reset code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                className="h-12 tracking-[0.5em] text-center text-lg font-mono"
                autoComplete="one-time-code"
                autoFocus
              />
            )}

            <div className="space-y-3">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordRequirements password={password} />
            </div>

            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-12"
              autoComplete="new-password"
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 gradient-gold text-primary-foreground font-semibold shadow-glow hover:opacity-90"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Updating...
                </span>
              ) : (
                'Update password'
              )}
            </Button>

            {!isLegacy && (
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isSending}
                className="block w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors mt-2"
              >
                {isSending ? 'Sending…' : "Didn't get a code? Resend"}
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
